import { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, ShieldAlert } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import { C } from "../../theme/tokens";

// pdfjs-dist 4.x+ dropped the classic worker-entry module (no more
// `pdfjs-dist/legacy/build/pdf.worker.entry` to import) in favour of
// pointing workerSrc at the worker file's own URL. This `new URL(...,
// import.meta.url)` form is webpack 5's asset-module pattern (CRA 5 /
// react-scripts 5 ships webpack 5, so this works without ejecting) —
// it resolves and bundles the worker file, then hands pdf.js a URL to
// spin it up as a separate script, rather than us importing its code
// directly like the old worker-entry package did.
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/legacy/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

/* ---------------------------------------------------------------
   PROTECTED PDF VIEWER
   ------------------------------------------------------------------
   What this does and does not do — read before wiring it up:

   - The PDF bytes are fetched once via an authenticated request and
     kept only in memory (never written to a blob: URL, never linked
     from the DOM, never handed to a native <embed>/<iframe> PDF
     plugin). That's what removes the *one-click* "Save as..." /
     browser print button that a plain <iframe src="file.pdf"> would
     hand out for free.
   - Every page is rasterised with pdf.js onto a plain <canvas>. No
     text layer is rendered, so there's no text to select or Ctrl+C.
   - A watermark (viewer identity + timestamp) is drawn INTO the
     canvas pixels, not as a removable DOM overlay — so it survives
     "inspect element" and shows up in any screenshot/photo taken of
     the screen, which is what actually discourages leaks.
   - Right-click, drag, and the common save/print keyboard shortcuts
     are blocked while the viewer is open, and a best-effort check
     for an open devtools panel blurs the content.

   pdfjs-dist ships as a regular npm dependency (`npm install
   pdfjs-dist`) — it's ESM-only from 4.x onward, no CommonJS build, no
   separate worker-entry package to import. CRA's react-scripts@5
   (webpack 5) handles that fine via the `new URL(..., import.meta.url)`
   pattern above. Importing it directly like this, instead of loading
   it from a CDN <script> tag, means there's no third-party URL/version
   to go stale or 404 on you.

   What this can NEVER do: stop someone from photographing their
   screen, or from using OS-level screen recording. No web-based
   viewer can prevent that — treat the watermark as the real
   deterrent (it identifies the leaker), not the canvas rendering.
------------------------------------------------------------------ */

// Best-effort, heuristic-only devtools probe (window chrome vs. viewport
// delta). It has false positives on some zoom levels / docked layouts —
// that's an acceptable trade-off for a soft deterrent, not a gate.
function useDevtoolsGuess() {
  const [suspect, setSuspect] = useState(false);
  useEffect(() => {
    const THRESHOLD = 160;
    const id = setInterval(() => {
      const dw = window.outerWidth - window.innerWidth;
      const dh = window.outerHeight - window.innerHeight;
      setSuspect(dw > THRESHOLD || dh > THRESHOLD);
    }, 800);
    return () => clearInterval(id);
  }, []);
  return suspect;
}

function drawWatermark(ctx, canvasWidth, canvasHeight, label) {
  ctx.save();
  ctx.globalAlpha = 0.14;
  ctx.fillStyle = "#7A8578";
  ctx.font = "600 15px 'Space Grotesk', sans-serif";
  ctx.textAlign = "center";
  const stepX = 230;
  const stepY = 130;
  for (let y = -stepY; y < canvasHeight + stepY; y += stepY) {
    for (let x = -stepX; x < canvasWidth + stepX; x += stepX) {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(-Math.PI / 8);
      ctx.fillText(label, 0, 0);
      ctx.restore();
    }
  }
  ctx.restore();
}

/**
 * @param {() => Promise<ArrayBuffer>} fetchPdfBytes  loads the raw PDF bytes
 *   (wire this to authFetch(...).arrayBuffer() — see LessonPdfScreen).
 * @param {string} watermarkLabel  text baked into every page, e.g.
 *   "Іван Petrenko · id 348219044 · 11.08.2026 14:02"
 */
export function ProtectedPdfViewer({ fetchPdfBytes, watermarkLabel, t }) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const pdfDocRef = useRef(null);
  const renderTaskRef = useRef(null);

  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.15);
  const devtoolsSuspect = useDevtoolsGuess();

  // Load the document once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const bytes = await fetchPdfBytes();
        if (cancelled) return;
        const doc = await pdfjsLib.getDocument({ data: bytes }).promise;
        if (cancelled) return;
        pdfDocRef.current = doc;
        setNumPages(doc.numPages);
        setStatus("ready");
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[ProtectedPdfViewer] failed to load lesson PDF:", e);
        if (!cancelled) setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
      pdfDocRef.current?.destroy?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderPage = useCallback(async () => {
    const doc = pdfDocRef.current;
    const canvas = canvasRef.current;
    if (!doc || !canvas) return;
    renderTaskRef.current?.cancel?.();

    const page = await doc.getPage(pageNum);

    // The bug this fixes: the canvas backing store was being sized
    // 1:1 with CSS/PDF pixels (`viewport.width/height` at plain
    // `scale`), with no CSS width/height override — so on any
    // HiDPI/Retina screen (devicePixelRatio 2, 3...) the browser was
    // stretching that low-res raster across 2x/3x as many physical
    // pixels, which is exactly what reads as "blurry"/"eats quality".
    // Fix: raster at scale * devicePixelRatio into a bigger backing
    // store, then pin the *displayed* CSS size back down to the
    // original (unscaled) viewport so it looks the same size on
    // screen, just sharp. Capped at 3x so a 3-4x-DPI phone doesn't
    // blow the canvas up to a huge memory footprint for no visible gain.
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const cssViewport = page.getViewport({ scale });
    const renderViewport = page.getViewport({ scale: scale * dpr });

    canvas.width = renderViewport.width;
    canvas.height = renderViewport.height;
    canvas.style.width = `${cssViewport.width}px`;
    canvas.style.height = `${cssViewport.height}px`;

    const ctx = canvas.getContext("2d");

    const task = page.render({ canvasContext: ctx, viewport: renderViewport });
    renderTaskRef.current = task;
    try {
      await task.promise;
    } catch (e) {
      if (e?.name === "RenderingCancelledException") return;
      throw e;
    }
    // Watermark geometry (font size, spacing) is tuned in CSS-pixel
    // terms, so draw it through a dpr-scaled context rather than
    // against the raw (dpr-multiplied) canvas.width/height — otherwise
    // it'd render dpr× too small and dpr× too dense on HiDPI screens.
    ctx.save();
    ctx.scale(dpr, dpr);
    drawWatermark(ctx, cssViewport.width, cssViewport.height, watermarkLabel);
    ctx.restore();
  }, [pageNum, scale, watermarkLabel]);

  useEffect(() => {
    if (status === "ready") renderPage();
  }, [status, renderPage]);

  // Block the obvious "save the file" / "print it" / "select all, copy"
  // paths while this viewer is mounted. Scoped to keydown on the
  // container plus a capturing document listener so it also catches
  // focus sitting outside the canvas itself.
  useEffect(() => {
    const blockShortcuts = (e) => {
      const k = e.key?.toLowerCase();
      const mod = e.ctrlKey || e.metaKey;
      if (mod && (k === "s" || k === "p" || k === "u")) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (mod && e.shiftKey && (k === "i" || k === "j" || k === "c")) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (k === "printscreen") {
        e.preventDefault();
      }
    };
    document.addEventListener("keydown", blockShortcuts, true);
    return () => document.removeEventListener("keydown", blockShortcuts, true);
  }, []);

  const blockContextMenu = (e) => e.preventDefault();
  const blockDrag = (e) => e.preventDefault();

  const goPrev = () => setPageNum((p) => Math.max(1, p - 1));
  const goNext = () => setPageNum((p) => Math.min(numPages, p + 1));
  const zoomIn = () => setScale((s) => Math.min(2.4, s + 0.2));
  const zoomOut = () => setScale((s) => Math.max(0.6, s - 0.2));

  return (
    <div
      ref={containerRef}
      onContextMenu={blockContextMenu}
      style={{
        userSelect: "none",
        WebkitUserSelect: "none",
        position: "relative",
        borderRadius: 16,
        border: `1px solid ${C.hairline}`,
        background: C.panel1,
        overflow: "hidden",
      }}
    >
      {/* Belt-and-suspenders: if a browser somehow forces a print
          render, blank the page instead of letting the canvas print. */}
      <style>{"@media print { .pj-pdf-viewer { display: none !important; } }"}</style>

      <div
        className="pj-pdf-viewer"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
          borderBottom: `1px solid ${C.hairline}`,
          gap: 10,
        }}
      >
        <button onClick={goPrev} disabled={pageNum <= 1} style={navBtnStyle}>
          <ChevronLeft size={16} color={C.primary} />
        </button>
        <div style={{ fontSize: 12.5, color: C.secondary, fontWeight: 600 }}>
          {status === "ready" ? `${pageNum} / ${numPages}` : ""}
        </div>
        <button onClick={goNext} disabled={pageNum >= numPages} style={navBtnStyle}>
          <ChevronRight size={16} color={C.primary} />
        </button>
        <div style={{ flex: 1 }} />
        <button onClick={zoomOut} style={navBtnStyle}>
          <ZoomOut size={15} color={C.primary} />
        </button>
        <button onClick={zoomIn} style={navBtnStyle}>
          <ZoomIn size={15} color={C.primary} />
        </button>
      </div>

      <div
        style={{
          position: "relative",
          minHeight: 320,
          display: "flex",
          justifyContent: "center",
          padding: "18px 12px",
          overflow: "auto",
          maxHeight: "72vh",
          filter: devtoolsSuspect ? "blur(22px)" : "none",
          transition: "filter .12s ease",
        }}
      >
        {status === "loading" && (
          <div style={{ color: C.secondary, fontSize: 13, padding: "60px 0" }}>{t?.lesson_loading || "Завантаження..."}</div>
        )}
        {status === "error" && (
          <div style={{ color: C.secondary, fontSize: 13, padding: "60px 0", textAlign: "center", maxWidth: 320 }}>
            {t?.lesson_error || "Не вдалося завантажити урок. Спробуйте ще раз."}
          </div>
        )}
        <canvas
          ref={canvasRef}
          onDragStart={blockDrag}
          style={{
            display: status === "ready" ? "block" : "none",
            borderRadius: 8,
            boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
            pointerEvents: "none", // no click-drag selection surface at all
          }}
        />

        {devtoolsSuspect && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              background: "rgba(0,0,0,0.55)",
              color: C.primary,
              fontSize: 12.5,
              textAlign: "center",
              padding: 20,
            }}
          >
            <ShieldAlert size={22} color={C.violetBright} />
            {t?.lesson_devtools_warning || "Контент приховано, поки відкриті інструменти розробника."}
          </div>
        )}
      </div>
    </div>
  );
}

const navBtnStyle = {
  width: 30,
  height: 30,
  borderRadius: 8,
  border: `1px solid ${C.hairline}`,
  background: C.panel2,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};
